import { createApp } from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import "@/scss/styles.scss";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { PiniaSharedState } from "pinia-shared-state";
import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { md1 } from "vuetify/blueprints";
import Vue3Toastify, { type ToastContainerOptions } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { CkeditorPlugin } from "@ckeditor/ckeditor5-vue";
import "vue-advanced-cropper/dist/style.css";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
pinia.use(PiniaSharedState({ enable: true, initialize: true, type: "native" }));

const vuetify = createVuetify({
  components,
  directives,
  blueprint: md1,
  defaults: {
    VTextField: { variant: "outlined" },
    VFileInput: { variant: "outlined" },
    VCombobox: { variant: "outlined" },
    VSelect: { variant: "outlined" },
    VTextarea: { variant: "outlined" },
    VCheckbox: { color: "primary" },
    VRadio: { color: "primary" },
    VSwitch: { color: "primary" },
    VProgressCircular: { indeterminate: true },
  },
});

const app = createApp(App);

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    app,
    dsn: "https://3cd14e697efd9f4409b82adc43655dd5@sentry.onix.team/229",
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [
          "onix-systems-nexusmedx-admin.staging.onix.ua/",
          /^\//,
        ],
      }),
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const isChunkError =
        error instanceof Error &&
        /Loading chunk [\d]+ failed/.test(error.message);
      if (isChunkError) {
        return null;
      }
      return event;
    },
    tracesSampleRate: 1.0,
    environment: "development",
    logErrors: true,
  });
}

app
  .use(pinia)
  .use(router)
  .use(Vue3Toastify, {
    autoClose: 3000,
    clearOnUrlChange: false,
    newestOnTop: true,
  } as ToastContainerOptions)
  .use(vuetify)
  .use(CkeditorPlugin)
  .mount("#app");
